import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { DataGridPro, GridRowModes } from '@mui/x-data-grid-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import {
  getParentIssueQuery,
  saveConfirmationReinit,
} from '../../../../../../packages/common/api';
import ArrowDownIcon from '../../../../../../packages/common/shared-ui/src/icons/ArrowDownIcon';
import ChevronIcon from '../../../../../../packages/common/shared-ui/src/icons/ChevronIcon';
import ParentIssueDropdown from '../../../../../../packages/common/shared-ui/src/icons/ParentIssueDropdown';
import SortIcon from '../../../../../../packages/common/shared-ui/src/icons/SortIcon';
import { useTranslation } from '../../../../../../packages/common/utils/getLocalString';
import { LanguageMap } from '../../../../../../packages/common/utils/languageMap';
import { store } from '../../../../app/redux';

// Original class: rowAutocomlpete
const StyledRowAutocomlpete = styled(Autocomplete, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '&.MuiAutocomplete-root': {
    width: '100%',
  },
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    padding: '0',
    lineHeight: '20px',
    height: 'auto',
    '& .MuiAutocomplete-endAdornment': {
      right: 0,
      '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator':
        {
          '& .MuiSvgIcon-root': {
            width: '20px',
            height: '20px',
          },
        },
      '& .MuiSvgIcon-root': {
        width: '20px',
        height: '20px',
        position: 'static',
      },
    },
    '& .MuiAutocomplete-input': {
      fontSize: '14px',
      lineHeight: '20px',
      color: '#7174AC',
      padding: '0',
      height: 'auto',
      ...(changedStyle && {
        color: '#212346',
      }),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      padding: 0,
      border: 'none',
    },
  },
  '&.Mui-expanded': {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '16px 16px 0 0',
      '& .MuiOutlinedInput-notchedOutline': {
        padding: 0,
      },
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
  '&.Mui-focused': {
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      display: 'none',
    },
  },
  '& + .MuiAutocomplete-popper': {
    minWidth: 'auto!important',
    '& .MuiPaper-root.MuiAutocomplete-paper': {
      borderRadius: '0 0 16px 16px',
      borderTop: 'none',
      boxShadow: '0px 5px 10px -8px #A0A1AA66',
      '& .MuiAutocomplete-noOptions': {
        padding: '8px 16px',
        color: '#212346',
        fontSize: '12px',
        lineHeight: '20px',
      },
      '& .MuiAutocomplete-listbox': {
        padding: '0',
        '& li': {
          whiteSpace: 'normal',
          fontSize: '12px',
          lineHeight: '20px',
          color: '#212346',
          '&.Mui-focused': {
            backgroundColor: 'transparent',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '&.Mui-selected': {
            backgroundColor: '#DCDFF4',
          },
        },
        '&::-webkit-scrollbar': {
          width: '2px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#7174AC',
          borderRadius: '5px',
        },
      },
    },
  },
}));
// Original class: rowInput
const StyledRowInput = styled(TextField, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '& .MuiInputBase-root.MuiInput-root': {
    fontSize: '14px',
    lineHeight: '20px',
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiInputBase-input.MuiInput-input': {
      color: '#7174AC',
      padding: 0,
      '&::placeholder': {
        color: '#7174AC',
        opacity: 1,
      },
      ...(changedStyle && {
        color: '#212346',
      }),
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: selectLabel
const StyledSelectLabel = styled(InputLabel)(theme => ({
  '&.MuiFormLabel-root.MuiInputLabel-root': {
    color: '#7174AC',
    fontSize: '14px',
    lineHeight: '20px',
    transform: 'none',
    '&.Mui-focused': {
      transform: 'none',
    },
    '&.MuiFormLabel-filled': {
      display: 'none',
    },
  },
}));
// Original class: rowSelect
const StyledRowSelect = styled(Select, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '&.MuiInputBase-root.MuiInput-root': {
    marginTop: 0,
    width: '100%',
    fontSize: '14px',
    lineHeight: '20px',
    height: '100%',
    padding: '0',
    color: '#7174AC',
    ...(changedStyle && {
      color: '#212346',
    }),
    '&.changed': {
      color: '#212346',
    },
    '&::before': {
      borderBottom: 'none',
    },
    '&:hover': {
      '&::before': {
        borderBottom: 'none',
      },
    },
    '& .MuiSelect-select': {
      padding: 0,
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root.MuiSelect-icon': {
      width: '20px',
      height: '20px',
      position: 'static',
    },
    '& .MuiSelect-nativeInput': {
      padding: '0',
    },
    '&.Mui-error': {
      border: '1px solid #E03737',
      '&::after': {
        display: 'none',
      },
    },
  },
}));
// Original class: paperDropdown
const StyledPaperDropdown = styled(Paper)(theme => ({
  '&.MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
    position: 'absolute',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: '300px',
    borderRadius: '0 0 16px 16px',
    borderTop: '1px solid #E4E4EF',
    boxShadow: '0px 5px 10px -8px #A0A1AA66',
    '&::-webkit-scrollbar': {
      width: '2px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#7174AC',
      borderRadius: '5px',
    },
    '& .MuiList-root.MuiMenu-list': {
      padding: '0',
      '& li': {
        fontSize: '14px',
        lineHeight: '20px',
        color: '#212346',
        '&.MuiButtonBase-root.MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: '#DCDFF4',
          },
        },
      },
    },
  },
}));
// Original class: datePicker
const StyledDatePicker = styled(DatePicker, {
  shouldForwardProp: prop => !['changedStyle'].includes(prop),
})(({ theme, changedStyle }) => ({
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: '100%',
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      padding: 0,
      color: '#7174AC',
      ...(changedStyle && {
        color: '#212346',
      }),
      fontSize: '14px',
    },
    '&.Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  },

  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));
// Original class: DataGridTableRoot
const StyledDataGridTableRoot = styled(DataGridPro)(({ theme }) => ({
  width: '100%',
  overflow: 'hidden',

  '&.MuiDataGrid-root': {
    border: 0,
    '& .MuiDataGrid-columnHeaders': {
      borderBottom: '1px solid #E4E4EF',
      '& .MuiBadge-badge': {
        display: 'none',
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
        '&.tableGroupGanttHeaderCell': {},
        '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            borderLeft: '1px solid #E4E4EF',
            '&.actions': {
              borderLeft: 'none',
            },
          },
        },
      },
      '& .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor.MuiDataGrid-columnHeaderCheckbox':
        {
          minWidth: '36px!important',
          maxWidth: '36px!important',
          '& + .MuiDataGrid-columnHeader.MuiDataGrid-withBorderColor': {
            paddingLeft: 0,
          },
        },
    },
    '& .MuiDataGrid-virtualScroller': {
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#7174AC',
        borderRadius: '5px',
      },
    },
    '& .MuiDataGrid-row': {
      width: '100%',
      borderBottom: '1px solid #E4E4EF',
      '& .MuiDataGrid-rowReorderCellContainer': {
        borderRight: '1px solid #E4E4EF',
      },
      '&.errorRow': {
        outline: '1px solid #E03737',
      },
      '& .MuiDataGrid-cell': {
        '&.tableCell': {
          overflow: 'visible',
          '&.parent': {
            paddingLeft: 0,
          },
          '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
            '& + .MuiDataGrid-cell.tableCell': {
              borderLeft: '1px solid #E4E4EF',
            },
          },
          '&.tableGroupCell': {
            overflow: 'visible',
            whiteSpace: 'nowrap',
            '&:not(.MuiDataGrid-columnHeaderCheckbox)': {
              '& + .MuiDataGrid-cell.tableCell': {
                borderLeft: '1px solid transparent',
              },
            },
          },
          '&.tableGroupGanttCell': {
            padding: '10px 10px 10px 0',
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& .MuiDataGrid-cell.MuiDataGrid-cellCheckbox': {
        minWidth: '36px!important',
        maxWidth: '36px!important',
        '& + .MuiDataGrid-cell': {
          paddingLeft: 0,
        },
      },
      '&.MuiDataGrid-row--editing': {
        boxShadow: 'none',
      },
    },
    '& .MuiDataGrid-row.groupRow': {
      '&.confirmationRow': {
        borderTop: '4px solid #E4E4EF',
        '&.first-row': {
          borderTop: 'none',
        },
      },
      '& .MuiDataGrid-cellCheckbox': {
        boxShadow: 'none',
        '& .MuiCheckbox-root': {
          visibility: 'hidden',
        },
      },
    },
    '& .MuiDataGrid-row .MuiDataGrid-cell:last-child': {
      borderRight: 0,
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-row.Mui-selected.Mui-hovered': {
      backgroundColor: '#DCDFF4',
    },
    '& .MuiDataGrid-main .MuiDataGrid-iconButtonContainer .MuiButtonBase-root':
      {
        marginLeft: theme.spacing(2),
      },
    '& .MuiCheckbox-root': {
      '& .MuiSvgIcon-root': {
        width: '12px',
      },
    },
    '& .MuiDataGrid-columnSeparator': {
      '& .MuiSvgIcon-root': {
        width: '24px',
      },
    },
    '& .MuiDataGrid-cell': {
      borderBottom: 'none',
      '&.MuiDataGrid-cell--editing': {
        boxShadow: 'none',
        '&:focus-within': {
          outline: 'none',
        },
      },
      '&.tableCell': {
        wordBreak: 'break-word',
        borderColor: 'transparent',
        position: 'relative',
        padding: '10px',
        '&.tableSelectCell': {
          overflow: 'visible',
        },
        '& a': {
          position: 'relative',
        },
        '&:last-child': {
          boxShadow: 0,
        },
        '& .MuiCheckbox-root': {
          padding: 0,
        },
        '&.cellEdit': {
          padding: 0,
        },
        '&.selectCell': {
          padding: 0,
        },
      },
      '&.actions': {
        padding: 0,
        '& .MuiDataGrid-actionsCell': {
          height: '100%',
          width: '100%',
          gridGap: 0,
        },
        '& .MuiButtonBase-root.MuiIconButton-root': {
          height: '100%',
          borderRadius: '0',
          padding: '0',
          flex: 1,
          '& .MuiSvgIcon-root': {
            width: '16px',
            height: '16px',
          },
          '& + .MuiButtonBase-root.MuiIconButton-root': {
            borderLeft: '1px solid #E4E4EF',
          },
        },
      },
      '& a': {
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
    '& .MuiDataGrid-cellCheckbox': {
      borderColor: 'transparent',
    },
    '& .MuiDataGrid-main': {
      borderRadius: '16px',
      backgroundColor: '#fff',
    },
  },

  '& .MuiDataGrid-footerContainer': {
    marginTop: '20px',
    minHeight: 'auto',
    border: 'none',
    justifyContent: 'flex-start',
    '& .MuiToolbar-root.MuiTablePagination-toolbar': {
      minHeight: 'auto',
      padding: 0,
      '& .MuiPagination-root': {
        marginLeft: 0,
        '& li': {
          fontSize: '16px',
          lineHeight: '20px',
        },
        '& li + li': {
          marginLeft: '2px',
        },
        '& .MuiButtonBase-root.MuiPaginationItem-root': {
          color: '#41424E',
          fontSize: '16px',
          lineHeight: '20px',
          borderRadius: '4px',
          minWidth: '28px',
          height: '28px',
          margin: 0,
          '&.Mui-selected': {
            backgroundColor: '#6270D0',
            color: '#FAFAFC',
            '&:hover': {
              color: '#FAFAFC',
              backgroundColor: '#6270D0',
            },
          },
          '& .MuiSvgIcon-root.MuiPaginationItem-icon': {
            width: 'auto',
            height: 'auto',
          },
          '&:hover': {
            color: '#6270D0',
            backgroundColor: 'transparent',
          },
        },
        '& .MuiPaginationItem-ellipsis': {
          fontSize: '16px',
          lineHeight: '20px',
          color: '#41424E',
          padding: 0,
          margin: 0,
          minWidth: ' 28px',
        },
      },
    },
  },
}));

const DateEditCell = ({
  value,
  changed,
  language,
  placeholder,
  onChangeValue,
}) => {
  const [resetKey, setResetKey] = useState(0);
  const [hasError, setHasError] = useState(false);

  const parsedValue = value && dayjs(value).isValid() ? dayjs(value) : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={language}>
      <StyledDatePicker
        key={resetKey}
        slotProps={{
          textField: {
            placeholder,
            onKeyDown: e => {
              // Clear the entire date on a single Backspace/Delete press
              // instead of deleting one section (day/month/year) at a time.
              if (e.key === 'Backspace' || e.key === 'Delete') {
                if (parsedValue) {
                  e.preventDefault();
                  e.stopPropagation();
                  setHasError(false);
                  onChangeValue(null);
                  setResetKey(k => k + 1);
                }
              }
            },
            onBlur: () => {
              // If the current input is invalid (partial/garbled date),
              // reset the picker to its default state (cleared value)
              // and force the internal input to re-sync via key remount.
              if (hasError) {
                setHasError(false);
                onChangeValue(null);
                setResetKey(k => k + 1);
              }
            },
          },
        }}
        changedStyle={changed}
        format={'DD.MM.YYYY'}
        value={parsedValue}
        onChange={(newValue, context) => {
          // Ignore intermediate invalid input; wait for blur to reset.
          if (context?.validationError) {
            setHasError(true);
            return;
          }
          setHasError(false);
          if (newValue === null) {
            // User cleared the field via backspace/delete
            onChangeValue(null);
            return;
          }
          if (dayjs.isDayjs(newValue) && newValue.isValid()) {
            onChangeValue(newValue);
          }
        }}
        onError={reason => {
          setHasError(!!reason);
        }}
        slots={{
          leftArrowIcon: () => (
            <ChevronIcon
              direction='left'
              viewBox='0 0 24 24'
              width={24}
              height={24}
            />
          ),
          rightArrowIcon: () => (
            <ChevronIcon
              direction='right'
              viewBox='0 0 24 24'
              width={24}
              height={24}
            />
          ),
          rightArrowButton: componentProps => (
            <IconButton disableRipple {...componentProps}>
              {componentProps.children}
            </IconButton>
          ),
          leftArrowButton: componentProps => (
            <IconButton disableRipple {...componentProps}>
              {componentProps.children}
            </IconButton>
          ),
        }}
      />
    </LocalizationProvider>
  );
};

const ConfirmationDocumentsTable = ({ confirmationDocuments, pageState }) => {
  const getLocalString = useTranslation();
  const [saveDocsLoading, setSaveDocsLoading] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(true);
  const [showSaveButton, setShowSaveButton] = useState(null);
  const [confirmationDocsColumns, setConfirmationDocsColumns] = useState([]);
  const [confirmationDocsRows, setConfirmationDocsRows] = useState([]);
  const [rowModesModel, setRowModesModel] = useState({});
  const [parentIssueOptions, setParentIssueOptions] = useState([]);
  const location = useLocation();
  const urlParams = useRef(new URLSearchParams(location.search));
  const state = store.getState();
  const handleGetRedirectUrl = useCallback(
    ({ id, columnType }) => {
      const issueLink = state.user.project
        ? `/issues/${id}?project=${state.user.project.redmineId}`
        : `/issues/${id}`;
      switch (columnType) {
        case 'project':
          return `${window.location.origin}/projects/${id}`;
        case 'issue':
        case 'issue.subject':
        case 'id':
        case 'issue_parent':
          return issueLink;
        default:
          return '';
      }
    },
    [state.user.project],
  );
  const [language, setLanguage] = useState('ru');

  const formattedGridRows = useCallback(
    data =>
      data.reduce((acc, row, index) => {
        const newCols = row.columns.reduce((columnAcc, item, i) => {
          let newItem = {
            ...columnAcc,
            [row.columns[i].fieldId]: row.columns[i].valueName,
            [row.columns[i].fieldId + 'Id']: row.columns[i].valueId,
          };
          if (item.fieldDefinition && item.fieldDefinition.values) {
            newItem.values = item.fieldDefinition.values;
          }
          return newItem;
        }, {});
        delete row.columns;
        return [
          ...acc,
          {
            ...newCols,
            ...row,
            id: row.rowId + index,
            level: row.services.level,
          },
        ];
      }, []),
    [],
  );

  useEffect(() => {
    window.localStorage.getItem('lang') &&
      setLanguage(LanguageMap.get(window.localStorage.getItem('lang')).lang);
  }, []);
  useEffect(() => {
    if (confirmationDocuments) {
      setLoadingDocuments(false);
      confirmationDocuments.columns &&
        setConfirmationDocsColumns(confirmationDocuments.columns);
      if (confirmationDocuments.rows) {
        setConfirmationDocsRows(formattedGridRows(confirmationDocuments.rows));
        const editableRows = confirmationDocuments.rows.reduce(
          (acc, item, index) => {
            return {
              ...acc,
              [item.rowId + index]: {
                mode: item.services.canEdit
                  ? GridRowModes.Edit
                  : GridRowModes.View,
              },
            };
          },
          {},
        );
        if (editableRows) {
          Object.values(editableRows).filter(
            item => item.mode === GridRowModes.Edit,
          ).length > 0 && setShowSaveButton(true);
          setRowModesModel(editableRows);
        }
      }
    }
  }, [confirmationDocuments, formattedGridRows]);

  const handleField = useCallback(
    (fieldValue, id, type) => {
      const numberFields = ['float', 'int', 'percent'];
      const index = confirmationDocsRows.findIndex(item => item.id === id);
      let updatedObject;
      fieldValue?.target &&
        (updatedObject = {
          ...confirmationDocsRows[index],
          ['confirmation.fieldRenewValue']: fieldValue.target.value,
          ['confirmation.fieldRenewValueId']: fieldValue.target.value,
        });
      numberFields.includes(type) &&
        (updatedObject['confirmation.fieldRenewValueId'] =
          fieldValue.target.value.replace(/\s+/g, ''));
      if (type === 'date') {
        const isValidDate =
          fieldValue && dayjs.isDayjs(fieldValue)
            ? fieldValue.isValid()
            : !!fieldValue && dayjs(fieldValue).isValid();
        const formatted =
          fieldValue && isValidDate
            ? dayjs(fieldValue).format('YYYY-MM-DD')
            : null;
        updatedObject = {
          ...confirmationDocsRows[index],
          ['confirmation.fieldRenewValue']: formatted,
          ['confirmation.fieldRenewValueId']: formatted,
        };
      }
      if (type === 'issue_parent') {
        updatedObject = {
          ...confirmationDocsRows[index],
          ['confirmation.fieldRenewValue']: fieldValue.valueName,
          ['confirmation.fieldRenewValueId']: fieldValue.valueId,
        };
        updatedObject['confirmation.fieldRenewValueSearch'] &&
          delete updatedObject['confirmation.fieldRenewValueSearch'];
      }
      updatedObject.changed = true;
      setConfirmationDocsRows([
        ...confirmationDocsRows.slice(0, index),
        updatedObject,
        ...confirmationDocsRows.slice(index + 1),
      ]);
    },
    [confirmationDocsRows],
  );

  const handleNumberField = useCallback(
    (fieldValue, id, field) => {
      const reg = new RegExp(/^[0-9,.]*$/);
      if (reg.test(fieldValue.target.value)) {
        handleField(fieldValue, id, field);
      }
    },
    [handleField],
  );

  const handleParentIssueChange = useCallback(
    async (value, id) => {
      const index = confirmationDocsRows.findIndex(item => item.id === id);
      let updatedObject = {
        ...confirmationDocsRows[index],
        ['confirmation.fieldRenewValueSearch']: value,
      };
      setConfirmationDocsRows([
        ...confirmationDocsRows.slice(0, index),
        updatedObject,
        ...confirmationDocsRows.slice(index + 1),
      ]);
      let params = {
        search: value,
      };
      if (value !== '') {
        urlParams.current.get('issues') &&
          (params.issues = urlParams.current
            .get('issues')
            .replaceAll(',', '||'));
        try {
          const response = await getParentIssueQuery(params);
          if (response) {
            setParentIssueOptions(response.data);
          }
        } catch (error) {
          console.error('ERROR WITH EDIT', error);
        }
      }
    },
    [confirmationDocsRows],
  );
  const formattedConfirmationDocsColumns = useCallback(() => {
    const smallFields = ['date', 'bool'];
    const editableColumns = ['confirmation.fieldRenewValue'];
    const numberFields = ['float', 'int', 'percent'];
    const hasRenewColumn = confirmationDocsColumns.find(
      column => column.field === 'fieldRenewValue',
    );
    const columns = confirmationDocsColumns.map(column => {
      return {
        field: column.fieldId,
        headerName: column.fieldName,
        renderHeader: props => (
          <Typography
            variant='h4'
            sx={{
              fontWeight: 700,
            }}
          >
            {props.colDef.headerName}
          </Typography>
        ),
        disableExport: false,
        flex: smallFields.includes(column.fieldType) ? 0 : 1,
        width: smallFields.includes(column.fieldType) && 150,
        cellClassName: props =>
          props.row.rowType === 'group'
            ? 'tableCell tableGroupCell'
            : props.row.services.fieldType === 'issue_parent'
              ? 'tableCell tableSelectCell'
              : 'tableCell',
        editable: editableColumns.includes(column.fieldId) && true,
        renderEditCell: props => {
          const index = confirmationDocsRows.findIndex(
            item => item.id === props.row.id,
          );
          if (props.row.services.fieldType === 'issue_parent') {
            return (
              <StyledRowAutocomlpete
                changedStyle={props.row.changed}
                noOptionsText={getLocalString('labels', 'notFound')}
                onChange={(event, newValue) =>
                  handleField(
                    newValue,
                    props.row.id,
                    props.row.services.fieldType,
                  )
                }
                disablePortal
                filterOptions={x => x}
                options={parentIssueOptions}
                sx={{ width: 253 }}
                disableClearable
                renderOption={(props, option) => {
                  return (
                    <MenuItem
                      {...props}
                      key={option.valueId}
                      value={option.valueId}
                    >
                      {option.valueName}
                    </MenuItem>
                  );
                }}
                value={
                  confirmationDocsRows[index]['confirmation.fieldRenewValue']
                    ? {
                        valueId:
                          confirmationDocsRows[index][
                            'confirmation.fieldRenewValueId'
                          ],
                        valueName:
                          confirmationDocsRows[index][
                            'confirmation.fieldRenewValue'
                          ],
                      }
                    : ''
                }
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                getOptionLabel={option => option.valueName || ''}
                inputValue={
                  confirmationDocsRows[index][
                    'confirmation.fieldRenewValueSearch'
                  ] ||
                  confirmationDocsRows[index][
                    'confirmation.fieldRenewValueSearch'
                  ] === ''
                    ? confirmationDocsRows[index][
                        'confirmation.fieldRenewValueSearch'
                      ]
                    : confirmationDocsRows[index][
                          'confirmation.fieldRenewValue'
                        ]
                      ? confirmationDocsRows[index][
                          'confirmation.fieldRenewValue'
                        ]
                      : ''
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    onChange={ev =>
                      handleParentIssueChange(ev.target.value, props.row.id)
                    }
                    placeholder={getLocalString('labels', 'select')}
                  />
                )}
                popupIcon={<ChevronIcon direction={'down'} />}
                slots={{
                  popper: props => (
                    <Popper {...props} placement='bottom-start'></Popper>
                  ),
                }}
              />
            );
          }
          if (numberFields.includes(props.row.services.fieldType)) {
            return (
              <StyledRowInput
                changedStyle={props.row.changed}
                defaultValue={
                  confirmationDocsRows[index]['confirmation.fieldRenewValue']
                    ? confirmationDocsRows[index][
                        'confirmation.fieldRenewValue'
                      ]
                    : ''
                }
                placeholder={getLocalString('labels', 'input')}
                variant='standard'
                onChange={event =>
                  handleField(event, props.row.id, props.row.services.fieldType)
                }
              ></StyledRowInput>
            );
          }
          if (
            props.row.services.fieldType === 'list' ||
            props.row.services.fieldType === 'user'
          ) {
            return (
              <FormControl sx={{ width: '100%' }}>
                <StyledSelectLabel id='select-label'>
                  {getLocalString('labels', 'select')}
                </StyledSelectLabel>
                <StyledRowSelect
                  changedStyle={props.row.changed}
                  native={false}
                  labelId='select-label'
                  label={getLocalString('labels', 'select')}
                  variant='standard'
                  sx={{ width: '100%', border: 'none' }}
                  MenuProps={{ slots: { paper: StyledPaperDropdown } }}
                  IconComponent={props => (
                    <ChevronIcon direction={'down'} {...props} />
                  )}
                  value={
                    confirmationDocsRows[index][
                      'confirmation.fieldRenewValueId'
                    ]
                      ? confirmationDocsRows[index][
                          'confirmation.fieldRenewValueId'
                        ]
                      : ''
                  }
                  onChange={event =>
                    handleField(
                      event,
                      props.row.id,
                      props.row.services.fieldType,
                    )
                  }
                >
                  {props.row.values.map((item, index) => (
                    <MenuItem key={index} value={item.valueId}>
                      {item.valueName}
                    </MenuItem>
                  ))}
                </StyledRowSelect>
              </FormControl>
            );
          }
          if (props.row.services.fieldType === 'date') {
            return (
              <DateEditCell
                value={
                  confirmationDocsRows[index]['confirmation.fieldRenewValueId']
                }
                changed={props.row.changed}
                language={language}
                placeholder={getLocalString('labels', 'input')}
                onChangeValue={value =>
                  handleField(value, props.row.id, props.row.services.fieldType)
                }
              />
            );
          }
          return (
            <StyledRowInput
              changedStyle={props.row.changed}
              defaultValue={
                confirmationDocsRows[index]['confirmation.fieldRenewValue']
                  ? confirmationDocsRows[index]['confirmation.fieldRenewValue']
                  : ''
              }
              placeholder={getLocalString('labels', 'input')}
              variant='standard'
              onChange={event =>
                handleField(event, props.row.id, props.row.services.fieldType)
              }
            ></StyledRowInput>
          );
        },
        renderCell: props => {
          const index = confirmationDocsRows.findIndex(
            item => item.id === props.row.id,
          );
          const linkFields = ['project', 'user', 'issue', 'issue_parent'];
          const stringValue = props.value;
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                padding:
                  props.colDef.field === 'confirmation.fieldName' &&
                  props.row.level !== 0
                    ? `0 0 0 ${
                        props.row.rowType !== 'normal' && props.row.level > 0
                          ? `${props.row.level * 16}px!important`
                          : props.row.rowType === 'normal' &&
                              props.row.level > 0
                            ? `${props.row.level * 16 + 16}px!important`
                            : 0
                      }`
                    : 0,
              }}
            >
              {props.row.rowType === 'group' &&
              props.colDef.field === 'confirmation.fieldName' ? (
                <>
                  <ArrowDownIcon></ArrowDownIcon>
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 600,
                      marginLeft: '4px',
                    }}
                  >
                    {props.row.rowName}
                  </Typography>
                </>
              ) : linkFields.includes(props.row.services.fieldType) &&
                props.field !== 'confirmation.fieldName' ? (
                <Link
                  to={handleGetRedirectUrl({
                    id: props.row[props.field + 'Id'],
                    columnType: props.row.services.fieldType,
                  })}
                >
                  {props.value}
                </Link>
              ) : props.field === 'confirmation.fieldRenewValue' ? (
                props.value
              ) : stringValue ? (
                stringValue
              ) : (
                ''
              )}
            </Box>
          );
        },
      };
    });
    return [...columns];
  }, [
    language,
    confirmationDocsColumns,
    confirmationDocsRows,
    getLocalString,
    handleField,
    handleGetRedirectUrl,
    handleParentIssueChange,
    parentIssueOptions,
  ]);

  const confirmationDocsColumnsValue = formattedConfirmationDocsColumns();

  const handleCellDoubleClick = useCallback(event => {
    event.stopPropagation();
  }, []);

  const validate = useCallback(() => {
    const numberFields = ['float', 'int', 'percent'];
    const reg = new RegExp(/^[0-9,.]*$/);
    const fields = confirmationDocsRows.filter(item =>
      numberFields.includes(item.services.fieldType),
    );
    const checkedFields = fields.filter(
      item =>
        item['confirmation.fieldRenewValueId'] &&
        !reg.test(item['confirmation.fieldRenewValueId']),
    );
    return checkedFields.length <= 0;
  }, [confirmationDocsRows]);

  const handleSaveClick = useCallback(async () => {
    const validation = validate();
    if (!validation) {
      return;
    }
    setSaveDocsLoading(true);
    const result = Object.groupBy(confirmationDocsRows, ({ rowId }) => rowId);
    const numberFields = ['float', 'int', 'percent'];
    const data = Object.entries(result).map(item => {
      const fieldsData = item[1]
        .filter(item => item.rowType !== 'group')
        .reduce((acc, item) => {
          let valueId = item['confirmation.fieldRenewValueId'];
          if (numberFields.includes(item.services.fieldType)) {
            item['confirmation.fieldRenewValueId'] === '' && (valueId = 0);
            valueId.toString().includes(',') &&
              (valueId = Number(valueId.replace(',', '.')));
          }
          return [
            ...acc,
            {
              fieldId: item['confirmation.fieldNameId'],
              valueId: valueId,
            },
          ];
        }, []);
      return {
        issueId: item[0],
        fields: fieldsData,
      };
    });
    try {
      const response = await saveConfirmationReinit(data);
      if (response) {
        const { data: res } = response;
        window.open(`/confirmations/${Number(res)}`, '_self');
      }
    } catch (error) {
      console.error('ERROR WITH GET', error);
    }
  }, [confirmationDocsRows, validate]);

  return (
    <>
      {loadingDocuments ? (
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '70px',
          }}
        >
          <CircularProgress color='secondary' />
        </Grid>
      ) : (
        confirmationDocsRows &&
        confirmationDocsColumns && (
          <Grid
            container
            sx={{
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {saveDocsLoading && (
              <Grid
                container
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  zIndex: 99,
                  position: 'absolute',
                }}
              >
                <CircularProgress color='secondary' />
              </Grid>
            )}
            <Grid
              sx={{
                borderRadius: '16px',
                marginTop: '20px',
                overflow: 'hidden',
                width: '100%',
                position: 'relative',
              }}
            >
              <StyledDataGridTableRoot
                localeText={{
                  noRowsLabel: getLocalString('labels', 'noData'),
                }}
                onRowDoubleClick={(params, event) =>
                  handleCellDoubleClick(event)
                }
                onCellDoubleClick={(params, event) =>
                  handleCellDoubleClick(event)
                }
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                disableColumnMenu
                columns={confirmationDocsColumnsValue}
                rows={confirmationDocsRows}
                getRowClassName={params =>
                  params.row.rowType === 'group' && params.isFirstVisible
                    ? 'groupRow confirmationRow first-row'
                    : params.row.rowType === 'group' && !params.isFirstVisible
                      ? 'groupRow confirmationRow'
                      : 'common-row'
                }
                pageSize={10}
                slots={{
                  columnUnsortedIcon: () => (
                    <SortIcon width='20' height='20' viewBox='0 0 20 20' />
                  ),
                  columnSortedAscendingIcon: () => (
                    <SortIcon
                      direction='up'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                    />
                  ),
                  columnSortedDescendingIcon: () => (
                    <SortIcon
                      direction='down'
                      width='20'
                      height='20'
                      viewBox='0 0 20 20'
                    />
                  ),
                }}
                getRowHeight={() => 'auto'}
                editMode='row'
                rowModesModel={rowModesModel}
              />
            </Grid>
            {showSaveButton && (
              <Grid>
                <Button
                  onClick={handleSaveClick}
                  variant='defaultBlueSecondary'
                  disableRipple
                  sx={{ marginTop: '8px' }}
                >
                  <Typography
                    variant='h4'
                    sx={{
                      fontWeight: 700,
                    }}
                  >
                    {getLocalString('buttons', 'save')}
                  </Typography>
                </Button>
              </Grid>
            )}
          </Grid>
        )
      )}
    </>
  );
};

export default ConfirmationDocumentsTable;
